"use client";

import { useEffect, useRef, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Page transition — ported from the codegrid madeinuxstudio Next.js
 * transition. A row of 20 vertical blocks slides across the viewport
 * to cover the page, the router push happens during the cover, then
 * the blocks slide out to reveal the new page.
 *
 * Intercepts every internal anchor click (`a[href^="/"]`) on the
 * page and runs the cover animation before navigating.
 */
const PageTransition = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const overlayRef = useRef(null);
  const blocksRef = useRef([]);
  const isTransitioning = useRef(false);
  const revealTimeoutRef = useRef(null);

  const coverPage = useCallback(
    (url) => {
      if (overlayRef.current) {
        overlayRef.current.style.pointerEvents = "auto";
      }

      gsap.to(blocksRef.current, {
        scaleX: 1,
        duration: 0.4,
        stagger: 0.02,
        ease: "power2.out",
        transformOrigin: "left",
        onComplete: () => {
          // Clean pinned elements (GSAP pin-spacers) before React
          // unmounts so parent.removeChild doesn't throw on a node
          // that's currently inside a spacer.
          try {
            ScrollTrigger.getAll().forEach((t) => t.kill(true));
          } catch {}
          // Let the browser finish one more frame of any in-flight
          // CSS transitions / motion exits before we tell Next to
          // tear down the tree. Without this, the unmount commit
          // races animations mid-cleanup and the first navigation
          // routinely failed with removeChild. Next's prefetch is
          // primed from RoutePrefetch by now, so the new route is
          // ready the instant we push.
          requestAnimationFrame(() => router.push(url));
        },
      });
    },
    [router]
  );

  const handleRouteChange = useCallback(
    (url) => {
      if (isTransitioning.current) return;
      isTransitioning.current = true;
      coverPage(url);
    },
    [coverPage]
  );

  const onAnchorClick = useCallback(
    (e) => {
      const target = e.currentTarget;
      if (isTransitioning.current) {
        e.preventDefault();
        return;
      }
      // Respect command/ctrl/shift/middle-click + new-tab targets.
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey ||
        e.button !== 0 ||
        target.target === "_blank"
      ) {
        return;
      }

      const href = target.getAttribute("href");
      if (!href || !href.startsWith("/")) return;
      // External link via href starting with //.
      if (href.startsWith("//")) return;

      // Same-path click: nothing to do.
      const url = new URL(target.href).pathname;
      if (url === pathname) return;

      e.preventDefault();
      handleRouteChange(url);
    },
    [pathname, handleRouteChange]
  );

  const revealPage = useCallback(() => {
    if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);

    gsap.set(blocksRef.current, { scaleX: 1, transformOrigin: "right" });

    gsap.to(blocksRef.current, {
      scaleX: 0,
      duration: 0.4,
      stagger: 0.02,
      ease: "power2.out",
      transformOrigin: "right",
      onComplete: () => {
        isTransitioning.current = false;
        if (overlayRef.current) {
          overlayRef.current.style.pointerEvents = "none";
        }
      },
    });

    // Safety net — if onComplete never fires (interrupted), force-reveal
    // after a second so the page isn't stuck behind the overlay.
    revealTimeoutRef.current = setTimeout(() => {
      if (!blocksRef.current.length) return;
      const first = blocksRef.current[0];
      if (first && gsap.getProperty(first, "scaleX") > 0) {
        gsap.to(blocksRef.current, {
          scaleX: 0,
          duration: 0.2,
          ease: "power2.out",
          transformOrigin: "right",
          onComplete: () => {
            isTransitioning.current = false;
            if (overlayRef.current) {
              overlayRef.current.style.pointerEvents = "none";
            }
          },
        });
      }
    }, 1200);
  }, []);

  useEffect(() => {
    // Build the 20 block elements once.
    if (!overlayRef.current) return;
    if (blocksRef.current.length === 0) {
      overlayRef.current.innerHTML = "";
      for (let i = 0; i < 20; i++) {
        const block = document.createElement("div");
        block.className = "page-transition-block";
        overlayRef.current.appendChild(block);
        blocksRef.current.push(block);
      }
      gsap.set(blocksRef.current, { scaleX: 0, transformOrigin: "left" });
    }

    revealPage();

    const links = document.querySelectorAll('a[href^="/"]');
    links.forEach((link) => link.addEventListener("click", onAnchorClick));

    return () => {
      links.forEach((link) =>
        link.removeEventListener("click", onAnchorClick)
      );
      if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
    };
  }, [pathname, onAnchorClick, revealPage]);

  return (
    <>
      <div ref={overlayRef} className="page-transition-overlay" aria-hidden />
      {children}
    </>
  );
};

export default PageTransition;
